import React from 'react';
import { FolderKanban, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Project } from '@/types';
import { toast } from '@/components/ui/use-toast';

interface AssignToProjectButtonProps {
  chatId: string;
  currentProjectId?: string | null;
}

const AssignToProjectButton = ({ chatId, currentProjectId }: AssignToProjectButtonProps) => {
  const { projects, assignChatToProject } = useSupabaseStore();

  const handleAssign = async (projectId: string | null) => {
    if (projectId === currentProjectId) return;
    try {
      await assignChatToProject(chatId, projectId);
      toast({
        title: 'Chat updated',
        description: `Chat has been ${projectId ? 'assigned to a project' : 'removed from project'}.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update chat project.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8"> {/* Changed from h-7 w-7 to h-8 w-8 */}
          <FolderKanban className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Assign to Project</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {projects.map((project: Project) => (
          <DropdownMenuItem key={project.id} onClick={() => handleAssign(project.id)}>
            <div className="flex items-center justify-between w-full">
              <span>{project.name}</span>
              {currentProjectId === project.id && <Check className="h-4 w-4 ml-2" />}
            </div>
          </DropdownMenuItem>
        ))}
        {projects.length === 0 && <DropdownMenuItem disabled>No projects found</DropdownMenuItem>}
        {currentProjectId && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleAssign(null)}>
              Remove from Project
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AssignToProjectButton;